import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ScrollText, ShieldCheck, Scale, Gavel, FileText, Lock, CreditCard, Truck,
  AlertTriangle, Ban, Copyright, Cookie, UserCog, Mail, RefreshCcw, Globe2,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { RETURNS_DAYS_DEFAULT } from "@/lib/site-config";

const SITE_URL = "https://fixhen.com";
const PAGE_URL = `${SITE_URL}/terms`;
const CONTACT_EMAIL = "info@contact.fixhen.com";
const SUPPORT_EMAIL = "info@contact.fixhen.com";
const LAST_UPDATED = "1 July 2026";
const EFFECTIVE_DATE = "1 July 2026";

const TOC: { id: string; label: string }[] = [
  { id: "about", label: "1. About FIXHEN & these Terms" },
  { id: "definitions", label: "2. Definitions" },
  { id: "eligibility", label: "3. Eligibility & account" },
  { id: "catalogue", label: "4. Catalogue, fitment & product information" },
  { id: "orders", label: "5. Orders, offer & acceptance" },
  { id: "pricing", label: "6. Prices, taxes & payment" },
  { id: "shipping", label: "7. Shipping, delivery & risk" },
  { id: "returns", label: "8. Returns & withdrawal" },
  { id: "warranty", label: "9. Legal guarantee & warranty" },
  { id: "liability", label: "10. Limitation of liability" },
  { id: "install", label: "11. Installation, fitness for purpose & motorsport" },
  { id: "conduct", label: "12. Acceptable use" },
  { id: "ip", label: "13. Intellectual property" },
  { id: "ugc", label: "14. User content & reviews" },
  { id: "privacy", label: "15. Privacy & cookies" },
  { id: "force", label: "16. Force majeure" },
  { id: "changes", label: "17. Changes to the Terms" },
  { id: "termination", label: "18. Suspension & termination" },
  { id: "law", label: "19. Governing law & jurisdiction" },
  { id: "misc", label: "20. Miscellaneous" },
  { id: "contact", label: "21. Contact" },
];

export const Route = createFileRoute("/terms")({
  head: () => {
    const jsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Terms & Conditions — FIXHEN",
        url: PAGE_URL,
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "FIXHEN", url: SITE_URL },
        about: { "@type": "Thing", name: "Terms of sale and use" },
        dateModified: "2026-07-01",
        datePublished: "2026-07-01",
        publisher: { "@type": "Organization", name: "FIXHEN", url: SITE_URL },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Terms & Conditions", item: PAGE_URL },
        ],
      },
    ];
    return {
      meta: [
        { title: "Terms & Conditions — FIXHEN" },
        {
          name: "description",
          content:
            "FIXHEN Terms & Conditions: the binding contract between you and FIXHEN covering orders, prices, delivery, warranty, liability and dispute resolution. EU-compliant, professionally drafted.",
        },
        { property: "og:title", content: "Terms & Conditions — FIXHEN" },
        {
          property: "og:description",
          content:
            "The binding contract between customers and FIXHEN: orders, payment, shipping, warranty, liability and jurisdiction.",
        },
        { property: "og:type", content: "article" },
        { property: "og:url", content: PAGE_URL },
        { name: "twitter:title", content: "Terms & Conditions — FIXHEN" },
        {
          name: "twitter:description",
          content: "The binding contract between customers and FIXHEN.",
        },
        { name: "robots", content: "index, follow" },
        { property: "og:image", content: "https://fixhen.com/og/home.jpg" },
        { name: "twitter:image", content: "https://fixhen.com/og/home.jpg" },
      ],
      links: [{ rel: "canonical", href: PAGE_URL }],
      scripts: jsonLd.map((data) => ({
        type: "application/ld+json",
        children: JSON.stringify(data),
      })),
    };
  },
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:py-16">
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li><Link to="/" className="hover:text-foreground">Home</Link></li>
            <li aria-hidden>/</li>
            <li className="text-foreground">Terms & Conditions</li>
          </ol>
        </nav>

        <header className="mb-10 border-b border-border pb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            <Scale className="h-3.5 w-3.5" /> Legal · Terms of Sale & Use
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-foreground sm:text-5xl">
            Terms & Conditions
          </h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">
            These Terms form the legally binding contract between you and FIXHEN. By placing
            an order, creating an account, or otherwise using our Site, you confirm that you
            have read, understood and accepted them. If you do not accept them, do not use
            the Site and do not place an order.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Effective date: <strong className="text-foreground">{EFFECTIVE_DATE}</strong></span>
            <span>Last updated: <strong className="text-foreground">{LAST_UPDATED}</strong></span>
            <span>Version: <strong className="text-foreground">2.0</strong></span>
          </div>
        </header>

        <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">On this page</p>
            <ol className="space-y-1.5 text-xs">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="block text-muted-foreground transition-colors hover:text-primary">
                    {t.label}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-black prose-headings:tracking-tight prose-h2:mt-12 prose-h2:scroll-mt-24 prose-h2:text-2xl prose-h3:text-lg prose-a:text-primary prose-strong:text-foreground">
            <div className="not-prose mb-8 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-100">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <strong>Please read carefully.</strong> Sections 9 (warranty), 10 (limitation of liability),
                  11 (installation and motorsport exclusion) and 19 (governing law and jurisdiction) contain
                  provisions that limit our liability and specify where disputes will be resolved. Consumer
                  statutory rights that cannot lawfully be limited are unaffected.
                </div>
              </div>
            </div>

            <h2 id="about"><ScrollText className="mr-2 inline h-5 w-5" />1. About FIXHEN & these Terms</h2>
            <p>
              The Site <strong>fixhen.com</strong> (the "Site") is operated by FIXHEN (the "Seller",
              "we", "our", "us"). These Terms & Conditions (the "Terms") govern (i) your access to
              and use of the Site, and (ii) every sale of goods concluded through the Site. They
              apply to consumers and, where indicated, to business customers ("B2B").
            </p>
            <p>
              The Terms are made available at the moment of order confirmation and remain accessible
              at <a href={PAGE_URL}>{PAGE_URL}</a>. We recommend that you save or print a copy for
              your records. Nothing in these Terms limits or excludes any statutory right of a
              consumer that cannot lawfully be limited or excluded.
            </p>

            <h2 id="definitions"><FileText className="mr-2 inline h-5 w-5" />2. Definitions</h2>
            <ul>
              <li><strong>"Customer", "you"</strong> — any natural or legal person who accesses the Site or places an Order.</li>
              <li><strong>"Consumer"</strong> — a natural person acting for purposes outside their trade, business, craft or profession.</li>
              <li><strong>"Order"</strong> — an offer by you to purchase Goods on the terms displayed at checkout.</li>
              <li><strong>"Contract"</strong> — the binding agreement formed on our written acceptance of an Order under section 5.</li>
              <li><strong>"Goods"</strong> — the automotive parts, 3D-printed items, accessories or related products offered on the Site.</li>
              <li><strong>"Parties"</strong> — you and FIXHEN, individually a "Party".</li>
            </ul>

            <h2 id="eligibility"><UserCog className="mr-2 inline h-5 w-5" />3. Eligibility & account</h2>
            <p>
              You must be at least 18 years old and legally capable of entering into a binding
              contract to place an Order. By registering an account you warrant that all information
              you supply is accurate, current and complete, and undertake to keep it up to date.
              You are responsible for maintaining the confidentiality of your credentials and for
              every activity carried out under your account. Notify us immediately at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> of any unauthorised use.
            </p>

            <h2 id="catalogue"><FileText className="mr-2 inline h-5 w-5" />4. Catalogue, fitment & product information</h2>
            <p>
              Product descriptions, technical data, photographs, renderings, OEM cross-references,
              vehicle-fitment tables and dimensional information are provided for guidance only.
              They are prepared with reasonable care but are <strong>not warranted to be exhaustive
              or free from error</strong>. Manufacturers may change specifications without notice.
              It is the Customer's responsibility to verify — before ordering — that the selected
              part is suitable for the intended vehicle, VIN, engine variant, market and use case.
            </p>
            <p>
              Colours, textures and finishes shown on screen depend on your device; minor deviations
              from the delivered Goods are inherent to the medium and do not constitute a defect.
              Where an OEM part number appears, it identifies the reference standard and, unless
              expressly stated, does <strong>not</strong> imply that the Goods are supplied by, or
              endorsed by, the vehicle manufacturer.
            </p>

            <h2 id="orders"><ShieldCheck className="mr-2 inline h-5 w-5" />5. Orders, offer & acceptance</h2>
            <p>
              Presentation of Goods on the Site constitutes an <em>invitatio ad offerendum</em>
              (invitation to treat), not a binding offer. Your Order is an offer to purchase on
              these Terms at the price and quantity shown at checkout. The automated order-received
              email is acknowledgement of receipt only and does <strong>not</strong> form a Contract.
            </p>
            <p>
              The Contract is concluded when we (i) send a separate order-confirmation or shipping
              notification, or (ii) despatch the Goods, whichever occurs first. We reserve the right
              to refuse or cancel any Order at our discretion — including, without limitation, in
              cases of pricing error, stock unavailability, suspected fraud, sanctions-list matches,
              export-control restrictions, or where we reasonably believe the Order breaches these
              Terms. In such cases any payment already taken is refunded in full without undue delay.
            </p>
            <p>
              <strong>Pricing errors.</strong> Where the price displayed is manifestly incorrect
              (e.g. mis-decimal, currency mistake or catalogue-mapping error) and the error should
              reasonably have been recognisable by the Customer, we may cancel the affected Order
              at any time before despatch without liability, refunding any sums paid.
            </p>

            <h2 id="pricing"><CreditCard className="mr-2 inline h-5 w-5" />6. Prices, taxes & payment</h2>
            <p>
              Prices are displayed in euro (EUR), inclusive of the Bulgarian VAT rate applicable at
              the time of Order unless another rate is shown at checkout for your destination.
              Shipping charges are shown separately at checkout. <strong>We ship every order on a
              Delivered Duty Paid (DDP) basis</strong> — FIXHEN is the importer of record and bears
              all customs duties, import taxes, brokerage and clearance formalities for the
              destination country. The total shown at checkout is the final amount you pay; no
              duties, taxes or fees are collected from you on delivery. For the avoidance of doubt,
              this DDP undertaking applies to the original delivery only; return shipping is
              governed by section 8 and the Returns Policy.
            </p>
            <p>
              Payment is due in full at the time of Order via the payment methods offered at
              checkout. Title to the Goods passes to the Customer only on full receipt of the
              purchase price, including shipping and any taxes ("retention of title"). Risk of loss
              or damage passes as set out in section 7.
            </p>
            <p>
              You warrant that you are the lawful holder of the payment instrument used and
              authorise us (or our payment service provider) to charge the total amount due. In
              case of chargeback made without a documented legal basis we reserve the right to
              recover the sum together with reasonable administrative costs.
            </p>

            <h2 id="shipping"><Truck className="mr-2 inline h-5 w-5" />7. Shipping, delivery & risk</h2>
            <p>
              Delivery times displayed on the Site are best-effort estimates and are not of the
              essence of the Contract. Carrier delays, customs inspections, weather events and
              force-majeure circumstances (section 16) do not entitle the Customer to cancel the
              Contract before a reasonable additional delivery period, communicated in writing,
              has elapsed without delivery.
            </p>
            <p>
              For Consumer sales within the EU, risk of loss or damage passes on physical delivery
              of the Goods to the Consumer or a third party (other than the carrier) designated by
              the Consumer, in accordance with Article 20 of Directive 2011/83/EU. For B2B sales,
              risk passes on handover of the Goods to the first carrier (Incoterms 2020 FCA our
              warehouse) unless otherwise agreed in writing.
            </p>
            <p>
              The Customer must inspect the shipment on delivery and report any visible transport
              damage or shortage to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> within
              <strong> 48 hours</strong>, attaching photographs of the outer packaging, inner
              packaging and Goods.
            </p>

            <h2 id="returns"><RefreshCcw className="mr-2 inline h-5 w-5" />8. Returns & withdrawal</h2>
            <p>
              Consumer customers benefit from a <strong>{RETURNS_DAYS_DEFAULT}-day right of
              withdrawal</strong> under our Returns Policy, which is incorporated into these Terms
              by reference and applies in addition to statutory rights under Articles 9–16 of
              Directive 2011/83/EU. For the eligibility conditions, excluded items, procedure,
              costs and refund timeline, please consult the{" "}
              <Link to="/returns">Returns Policy</Link>. B2B customers do not benefit from the
              statutory right of withdrawal and returns are accepted only where expressly agreed
              in writing.
            </p>

            <h2 id="warranty"><ShieldCheck className="mr-2 inline h-5 w-5" />9. Legal guarantee & warranty</h2>
            <p>
              Consumer Goods benefit from the two-year legal guarantee of conformity provided for
              under Directive (EU) 2019/771, running from delivery. Where a lack of conformity
              manifests within that period, we will — at our reasonable option and free of charge —
              repair, replace or refund the Goods, subject to the statutory conditions and the
              Goods having been correctly stored, installed and used in accordance with the
              manufacturer's specifications.
            </p>
            <p>
              No commercial warranty is offered beyond the legal guarantee unless expressly stated
              in writing on the product page or on the invoice. Warranty is <strong>void</strong>
              {" "}where the Goods have been: (a) misused, over-stressed or used outside their
              designed operating envelope; (b) fitted by an unqualified person; (c) modified,
              machined, painted, heat-treated or repaired without our written consent; (d) used
              for motorsport, off-road, track-day, commercial-fleet or any non-approved application
              (see section 11); or (e) damaged by accident, negligence, corrosion, contaminated
              fluids, electrical faults or other causes not attributable to the Goods.
            </p>

            <h2 id="liability"><Gavel className="mr-2 inline h-5 w-5" />10. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by applicable law, our aggregate liability arising
              out of or in connection with a Contract — whether in contract, tort (including
              negligence), restitution or otherwise — is limited to the total price paid by the
              Customer for the Goods giving rise to the claim.
            </p>
            <p>
              We are <strong>not liable</strong> for: (i) loss of profits, revenue, business
              opportunity, goodwill or anticipated savings; (ii) loss of use of a vehicle;
              (iii) recovery, towing, storage, workshop, diagnostic, labour or re-fitment costs;
              (iv) consequential damage to other vehicle components; (v) indirect, special,
              punitive or exemplary damages; (vi) delays caused by carriers, customs authorities
              or force-majeure events; or (vii) any loss that was not reasonably foreseeable to
              both Parties at the time the Contract was concluded.
            </p>
            <p>
              Nothing in these Terms excludes or limits our liability for (a) death or personal
              injury caused by our negligence; (b) fraud or fraudulent misrepresentation; (c) any
              liability that cannot lawfully be excluded, including consumer statutory rights and
              product-liability under Directive 85/374/EEC.
            </p>

            <h2 id="install"><AlertTriangle className="mr-2 inline h-5 w-5" />11. Installation, fitness for purpose & motorsport</h2>
            <p>
              The Goods are automotive spare parts and 3D-printed components that require
              professional installation and, in many cases, calibration, coding, alignment or
              road testing. <strong>We strongly recommend installation by a qualified
              technician.</strong> The Customer bears sole responsibility for verifying suitability
              for the intended vehicle and use, for compliance with local type-approval rules,
              and for the correctness of installation.
            </p>
            <p>
              Goods are supplied for on-road, private use in road-legal vehicles that comply with
              the applicable homologation regime. <strong>Use in motorsport, competition, off-road,
              track-day, commercial-fleet, ride-share, or any application not expressly approved by
              the manufacturer is at the Customer's sole risk</strong>, voids all warranty (section 9),
              and excludes any liability on our part to the fullest extent permitted by law.
            </p>

            <h2 id="conduct"><Ban className="mr-2 inline h-5 w-5" />12. Acceptable use</h2>
            <p>You agree not to, and not to attempt to:</p>
            <ul>
              <li>use the Site in breach of any applicable law, regulation or these Terms;</li>
              <li>scrape, crawl, index or otherwise systematically extract data from the Site without our prior written consent;</li>
              <li>reverse-engineer, decompile, disassemble or attempt to derive source code of any software forming part of the Site;</li>
              <li>interfere with the security or integrity of the Site, or introduce malware, worms, trojans or similar code;</li>
              <li>use the Site to send unsolicited communications, defamatory, obscene or unlawful content;</li>
              <li>impersonate any person, misrepresent your affiliation, or use another Customer's account without permission;</li>
              <li>place fraudulent, speculative or resale-oriented Orders, or engage in wardrobing (repeated purchase-and-return).</li>
            </ul>
            <p>Breach of this section entitles us to suspend or terminate your account and to pursue any legal remedy available to us.</p>

            <h2 id="ip"><Copyright className="mr-2 inline h-5 w-5" />13. Intellectual property</h2>
            <p>
              All content on the Site — including text, graphics, logos, icons, photographs, 3D
              models, renderings, product descriptions, code, software, sound and video files —
              is owned by FIXHEN or its licensors and is protected by copyright, database, trade
              mark and other intellectual-property rights. You are granted a limited, revocable,
              non-exclusive, non-transferable licence to view and print pages for the sole purpose
              of placing an Order or otherwise using the Site for its intended personal purpose.
            </p>
            <p>
              Any other use — including reproduction, republication, framing, mirroring, use for
              machine-learning training, or commercial exploitation — is prohibited without our
              prior written consent. Third-party trade marks, including OEM vehicle-manufacturer
              marks, are used strictly for identification and reference purposes and remain the
              property of their respective owners; their use does not imply endorsement.
            </p>

            <h2 id="ugc"><FileText className="mr-2 inline h-5 w-5" />14. User content & reviews</h2>
            <p>
              Where the Site allows you to submit reviews, photographs, questions or other
              material ("User Content"), you warrant that (i) you own or are entitled to submit
              the material, (ii) it is accurate, lawful, and not defamatory, infringing or
              obscene, and (iii) no third-party consent is required that you have not obtained.
              You grant us a worldwide, royalty-free, sub-licensable, perpetual and irrevocable
              licence to host, reproduce, adapt, translate, publish and communicate the User
              Content in connection with the Site, marketing materials and product pages. We
              may — but are not obliged to — moderate, edit or remove User Content at any time.
            </p>

            <h2 id="privacy"><Lock className="mr-2 inline h-5 w-5" />15. Privacy & cookies</h2>
            <p>
              Personal data collected in connection with the Site and Orders is processed in
              accordance with our Privacy Policy and the General Data Protection Regulation
              (EU) 2016/679. The Privacy Policy explains what data we collect, on what legal
              basis, for what purposes, retention periods, transfers, and how you can exercise
              your rights (access, rectification, erasure, portability, restriction, objection,
              and complaint to a supervisory authority). By using the Site you acknowledge
              having read the Privacy Policy.
            </p>

            <h2 id="force"><Globe2 className="mr-2 inline h-5 w-5" />16. Force majeure</h2>
            <p>
              Neither Party is liable for any failure or delay in performing its obligations
              (other than a payment obligation) caused by events beyond its reasonable control,
              including without limitation acts of God, natural disaster, epidemic, pandemic,
              war, armed conflict, terrorism, civil unrest, strike, industrial action, embargo,
              sanctions, cyber-attack, energy or telecommunications failure, or action of a
              governmental or supra-governmental authority. The affected Party shall notify the
              other in writing without undue delay and use reasonable efforts to mitigate the
              impact. Where the force-majeure event persists for more than sixty (60) days,
              either Party may terminate the affected Contract without liability, save for
              amounts due for Goods already delivered.
            </p>

            <h2 id="changes"><RefreshCcw className="mr-2 inline h-5 w-5" />17. Changes to the Terms</h2>
            <p>
              We may update these Terms from time to time to reflect changes in law, business
              practice or the functioning of the Site. The version applicable to a given
              Contract is the version in force at the moment the Order is placed. The current
              version is always available at <a href={PAGE_URL}>{PAGE_URL}</a> with an
              "Effective date". Continued use of the Site after publication of an updated
              version constitutes acceptance of the change for prospective use.
            </p>

            <h2 id="termination"><Ban className="mr-2 inline h-5 w-5" />18. Suspension & termination</h2>
            <p>
              We may suspend or terminate your access to the Site, or to any individual feature,
              at any time with or without notice, in the event of (a) actual or reasonably
              suspected breach of these Terms; (b) legal, regulatory or sanctions requirement;
              (c) risk to the security or integrity of the Site; or (d) discontinuation of the
              relevant service. Termination does not affect accrued rights or Contracts already
              concluded, which remain governed by these Terms.
            </p>

            <h2 id="law"><Gavel className="mr-2 inline h-5 w-5" />19. Governing law & jurisdiction</h2>
            <p>
              These Terms and any Contract are governed by the laws of the Republic of Bulgaria,
              excluding its rules on conflict of laws and excluding the United Nations Convention
              on Contracts for the International Sale of Goods (CISG). For Consumers habitually
              resident in another EU Member State, this choice does not deprive them of the
              protection afforded by the mandatory provisions of the law of that Member State
              under Article 6(2) of Regulation (EC) No 593/2008 (Rome I).
            </p>
            <p>
              The competent courts of Sofia, Bulgaria have exclusive jurisdiction over disputes
              between Parties acting as businesses. For disputes with Consumers, jurisdiction
              lies with the courts of Sofia or, at the Consumer's option, with the courts of
              their habitual residence within the EU. Consumers may also submit disputes to the
              European Commission's Online Dispute Resolution platform at{" "}
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">
                ec.europa.eu/consumers/odr
              </a>.
            </p>

            <h2 id="misc"><Cookie className="mr-2 inline h-5 w-5" />20. Miscellaneous</h2>
            <ul>
              <li><strong>Entire agreement.</strong> These Terms, together with the Returns Policy, Privacy Policy and any Order confirmation, constitute the entire agreement between the Parties and supersede any prior oral or written communication on the same subject-matter.</li>
              <li><strong>Severability.</strong> If any provision is held to be invalid, illegal or unenforceable, the remaining provisions continue in full force; the Parties will negotiate in good faith a valid provision achieving as closely as possible the original commercial intent.</li>
              <li><strong>No waiver.</strong> Failure or delay to enforce a right does not constitute a waiver of that right or of any other right.</li>
              <li><strong>Assignment.</strong> The Customer may not assign or transfer any rights or obligations under a Contract without our prior written consent. We may assign the Contract to an affiliate or to a successor in a corporate reorganisation, provided the Customer's rights are not adversely affected.</li>
              <li><strong>Notices.</strong> Notices to FIXHEN must be sent to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Notices to the Customer are sent to the email address on file.</li>
              <li><strong>Language.</strong> The authoritative version of these Terms is the English version; any translation is provided for convenience only.</li>
              <li><strong>Headings.</strong> Section headings are for convenience only and do not affect interpretation.</li>
            </ul>

            <h2 id="contact"><Mail className="mr-2 inline h-5 w-5" />21. Contact</h2>
            <p>
              Legal & compliance: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.<br />
              Customer support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> — response within one business day, Monday to Friday, excluding Bulgarian public holidays.
            </p>
          </article>
        </div>

        <aside className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-6">
          <h3 className="text-lg font-bold text-foreground">Questions about these Terms?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Our legal team is happy to clarify any clause before you order. Email{" "}
            <a className="font-medium text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            You may also review our <Link className="font-medium text-primary hover:underline" to="/returns">Returns Policy</Link>.
          </p>
        </aside>
      </main>
      <SiteFooter />
    </div>
  );
}